'use client';

import classNames from 'classnames';
import { useParams } from 'next/navigation';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { SlideType } from '@/api/config/types';
import { languages } from '@/app/[lang]/dictionaries';
import { CONTACT_US_ROUTE, PRODUCTS_ROUTE } from '@/app/[lang]/Router/routes';
import Carousel from '@/components/Carousel';
import Image from '@/components/core/Image';
import Link from '@/components/core/Link';
import Video from '@/components/core/Video';
import { DEFAULT_LANGUAGE } from '@/helpers';
import { useTranslation } from '@/hooks/useTranslations';

import './styles.scss';

interface SliderSectionProps {
  slides: SlideType[];
}

const languageLinks = languages.map(({ link }) => link);
const hasProtocol = (href: string) => /^[a-z][a-z\d+\-.]*:\/\//i.test(href);

const getLocalizedSliderUrl = (href: string | null, currentLang: string) => {
  if (!href) {
    return '';
  }

  try {
    const isAbsoluteUrl = hasProtocol(href);
    const parsedUrl = new URL(href, 'https://popokgaming.com');
    const pathParts = parsedUrl.pathname.split('/').filter(Boolean);
    const hasLanguagePath = languageLinks.includes(pathParts[0]);
    const pathWithoutLangParts = hasLanguagePath ? pathParts.slice(1) : pathParts;

    if (!hasLanguagePath && currentLang === DEFAULT_LANGUAGE) {
      return href;
    }

    const pathWithoutLang = pathWithoutLangParts.join('/');
    const localizedPath =
      currentLang === DEFAULT_LANGUAGE
        ? `/${pathWithoutLang}`
        : `/${currentLang}${pathWithoutLang ? `/${pathWithoutLang}` : ''}`;

    parsedUrl.pathname = localizedPath.replace(/\/+/g, '/') || '/';

    if (isAbsoluteUrl) {
      return parsedUrl.toString();
    }

    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch {
    return href;
  }
};

const SliderSection: FC<SliderSectionProps> = ({ slides }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isMouseOut, setIsMouseOut] = useState(true);
  const [mounted, setMounted] = useState(false);
  const { lang } = useParams();
  const { t } = useTranslation();
  const intervalId = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.style.transform = `translateX(-${activeSlide * 100}%)`;
    }
  }, [activeSlide]);

  useEffect(() => {
    if (!isMouseOut) {
      clearInterval(intervalId.current);
      return;
    }

    intervalId.current = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(intervalId.current);
  }, [activeSlide, isMouseOut]);

  useEffect(() => {
    setMounted(true);
  }, []);

  const carouselSettings = useMemo(
    () => ({
      dots: true,
      speed: 300,
      arrows: false,
      infinite: true,
      autoplay: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplaySpeed: 5000,
      customPaging: (i: number) => (
        <div className={'sliderBottomTab'}>
          <div className="sliderTopTab">
            <span className="sliderBottomTabCircle" />
          </div>
        </div>
      ),
      appendDots: (dots: React.ReactNode) => (
        <div className="homSliderBottomTabRow">
          <ul>{dots}</ul>
        </div>
      ),
    }),
    []
  );

  const sliderItems = useMemo(
    () =>
      slides.map((item, index) => {
        const isYoHealth = item.url?.includes('yohealth');
        const itemUrl = getLocalizedSliderUrl(item.url, lang as string);

        return (
          <div
            className={classNames('sliderImgWrp', {
              clickable: isYoHealth,
            })}
            key={item.url}
            onClick={() => {
              if (isYoHealth && item.url) {
                window.open(itemUrl, '_blank');
              }
            }}
          >
            {mounted && (
              <div className="slider-img-txt-wrp">
                {item.description && (
                  <div
                    className="slider-img-info"
                    dangerouslySetInnerHTML={{
                      __html: item.description,
                    }}
                  />
                )}
                {item.title && <p className="slider-img-title">{item.title}</p>}
              </div>
            )}
            <>
              <Link
                target={isYoHealth ? '_blank' : slides[activeSlide].target || '_self'}
                href={itemUrl || PRODUCTS_ROUTE.getPath(lang as string)}
                className="arrow-icon-home-slider"
                aria-label={`Go to ${itemUrl || PRODUCTS_ROUTE.getPath(lang as string)}`}
              >
                <i className="bc-i-go-to" />
              </Link>
              {item.logo.endsWith('.mp4') || item.logo.endsWith('.webm') ? (
                <Video
                  autoplay
                  height={400}
                  width={1300}
                  source={item.logo}
                  preload="metadata"
                  className="sliderImg"
                  onMouseOver={() => setIsMouseOut(false)}
                  onMouseLeave={() => setIsMouseOut(true)}
                />
              ) : (
                <Image
                  alt="#"
                  priority={index === 0}
                  // autoplay slider: next slides must be ready before they slide in
                  loading={index === 0 ? undefined : 'eager'}
                  width={1300}
                  height={400}
                  src={item.logo}
                  className="sliderImg"
                  onMouseOver={() => setIsMouseOut(false)}
                  onMouseLeave={() => setIsMouseOut(true)}
                />
              )}
            </>
          </div>
        );
      }),
    [activeSlide, lang, mounted, slides]
  );

  return (
    <div className="homeSliderBlock">
      <div className="bigContent">
        <div className="homSliderInner">
          <div className="homSliderImgCont">
            <Carousel slides={sliderItems} settings={carouselSettings} />
          </div>
        </div>
        <div className="levelUpContent">
          <div className="levelUpTitle">
            <h1 className="levelUpTitleText">{t('Homepage-H1')}</h1>
          </div>
          <div className="levelUpRight">
            <p className="levelUpDescText">{t('Homepage-Welcome description')}</p>
            <div className="levelUpBtnWrp">
              <Link
                href={CONTACT_US_ROUTE.getPath(lang as string)}
                className="button btnTypeBig btnTypeOrange"
              >
                <p className="mobBtnTxt">{t('Homepage-Button Contact us')}</p>
                <i className="bc-i-go-to btnIcon btnIconSmall" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SliderSection;
